import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Search, FileText } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import ReservationDialog from "@/components/ReservationDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Card, SectionHeader, PrimaryBtn, IconBtn, Field } from "./ui";

const EMPTY = { name: "", email: "", phone: "", street: "", postal_code: "", city: "", province: "", network: "", education_type: "", confession: "" };
const LIMIT = 25;
const ST_LABELS = { prospect: "Prospect", actif: "Actif", petit: "Petit client", regulier: "Client régulier", gros: "Gros client" };
const ST_COLORS = {
  prospect: "bg-slate-100 text-slate-600",
  actif: "bg-sky-100 text-sky-700",
  petit: "bg-emerald-100 text-emerald-700",
  regulier: "bg-violet-100 text-violet-700",
  gros: "bg-amber-100 text-amber-700",
};
const ST_FILTERS = [["all", "Tous"], ["prospect", "Prospect"], ["actif", "Actif"], ["petit", "Petit client"], ["regulier", "Client régulier"], ["gros", "Gros client"]];

export default function ClientsAdmin() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/clients", { params: { q: search, status: status === "all" ? "" : status, page, limit: LIMIT } })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .catch(() => {});
  }, [search, status, page]);
  useEffect(() => { load(); }, [load]);

  const doSearch = (e) => { e.preventDefault(); setPage(1); setSearch(q); };
  const applyStatus = (s) => { setStatus(s); setPage(1); };

  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Le nom de l'école est requis"); return; }
    try {
      if (editing.id) await api.patch(`/admin/clients/${editing.id}`, editing);
      else await api.post("/admin/clients", editing);
      toast.success("Client enregistré"); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Supprimer « ${c.name} » ?`)) return;
    try { await api.delete(`/admin/clients/${c.id}`); toast.success("Client supprimé"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <Card>
      <SectionHeader
        title={`Clients (${total})`}
        action={<PrimaryBtn data-testid="client-add-btn" onClick={() => setEditing({ ...EMPTY })}><Plus className="w-4 h-4" /> Nouveau client</PrimaryBtn>}
      />
      <form onSubmit={doSearch} className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="client-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par école, ville, province, réseau ou email…" className="pl-9 rounded-xl" />
        </div>
        <PrimaryBtn type="submit" data-testid="client-search-btn">Rechercher</PrimaryBtn>
        {search && <button type="button" onClick={() => { setQ(""); setSearch(""); setPage(1); }} className="text-sm text-slate-500 hover:text-slate-800 px-3">Réinitialiser</button>}
      </form>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="clients-status-filter">
        {ST_FILTERS.map(([val, label]) => (
          <button key={val} onClick={() => applyStatus(val)} data-testid={`clients-filter-${val}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${status === val ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {label}
          </button>
        ))}
      </div>

      {items.length === 0 ? <p className="text-slate-500">Aucun client trouvé.</p> : (
        <div className="overflow-x-auto" data-testid="admin-clients-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="py-3 pr-3">École</th>
                <th className="py-3 pr-3">Statut</th>
                <th className="py-3 pr-3">Email</th>
                <th className="py-3 pr-3">Ville</th>
                <th className="py-3 pr-3">Province</th>
                <th className="py-3 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const st = c.status || "prospect";
                return (
                  <tr key={c.id} className="border-b border-slate-50 align-top" data-testid={`client-row-${c.id}`}>
                    <td className="py-3 pr-3 font-semibold text-slate-800">{c.name}<br /><span className="text-xs font-normal text-slate-400">{c.network} · {c.phone}</span></td>
                    <td className="py-3 pr-3">
                      <span className={`text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap ${ST_COLORS[st]}`} data-testid={`client-status-${c.id}`}>{ST_LABELS[st]}</span>
                      {c.ateliers_count > 0 && <div className="text-[10px] text-slate-400 mt-1">{c.ateliers_count} atelier{c.ateliers_count > 1 ? "s" : ""} en {c.ateliers_year}</div>}
                    </td>
                    <td className="py-3 pr-3">{c.email ? <span className="text-slate-700">{c.email}</span> : <span className="text-xs italic text-amber-600">à compléter</span>}</td>
                    <td className="py-3 pr-3 text-slate-600">{c.city}</td>
                    <td className="py-3 pr-3 text-slate-600">{c.province}</td>
                    <td className="py-3 pr-3">
                      <div className="flex items-center justify-end gap-1">
                        <ReservationDialog
                          kind="atelier"
                          itemId={`client-${c.id}`}
                          itemTitle="Atelier Kwarizmix"
                          prefill={{ school: c.name, email: c.email }}
                          trigger={
                            <button data-testid={`client-devis-${c.id}`} className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full bg-sky-500 text-white hover:bg-sky-600 transition-colors">
                              <FileText className="w-3.5 h-3.5" /> Devis
                            </button>
                          }
                        />
                        <IconBtn icon={Pencil} color="sky" testid={`client-edit-${c.id}`} onClick={() => setEditing({ ...EMPTY, ...c })} />
                        <IconBtn icon={Trash2} color="rose" testid={`client-delete-${c.id}`} onClick={() => remove(c)} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6" data-testid="clients-pagination">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="text-sm font-bold px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 disabled:opacity-40">Précédent</button>
          <span className="text-sm text-slate-500">Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="text-sm font-bold px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 disabled:opacity-40">Suivant</button>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="client-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifier le client" : "Nouveau client"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Field label="Nom de l'école"><Input data-testid="client-field-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-xl" /></Field></div>
              <div className="col-span-2"><Field label="Email"><Input data-testid="client-field-email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="rounded-xl" placeholder="email@ecole.be" /></Field></div>
              <Field label="Téléphone"><Input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className="rounded-xl" /></Field>
              <Field label="Réseau"><Input value={editing.network} onChange={(e) => setEditing({ ...editing, network: e.target.value })} className="rounded-xl" /></Field>
              <div className="col-span-2"><Field label="Rue"><Input value={editing.street} onChange={(e) => setEditing({ ...editing, street: e.target.value })} className="rounded-xl" /></Field></div>
              <Field label="Code postal"><Input value={editing.postal_code} onChange={(e) => setEditing({ ...editing, postal_code: e.target.value })} className="rounded-xl" /></Field>
              <Field label="Ville"><Input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} className="rounded-xl" /></Field>
              <Field label="Province"><Input value={editing.province} onChange={(e) => setEditing({ ...editing, province: e.target.value })} className="rounded-xl" /></Field>
              <Field label="Type d'enseignement"><Input value={editing.education_type} onChange={(e) => setEditing({ ...editing, education_type: e.target.value })} className="rounded-xl" /></Field>
              <div className="col-span-2"><Field label="Confession"><Input value={editing.confession} onChange={(e) => setEditing({ ...editing, confession: e.target.value })} className="rounded-xl" /></Field></div>
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <button onClick={() => setEditing(null)} className="text-sm font-bold px-4 py-2 rounded-full bg-slate-100 text-slate-700">Annuler</button>
                <PrimaryBtn data-testid="client-save-btn" onClick={save}>Enregistrer</PrimaryBtn>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
